import React,{useState,useEffect,useCallback,useRef,useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {posAPI,productAPI,customerAPI,paymentAPI,currencyAPI,categoryAPI,inventoryAPI,businessAPI} from '../utils/api';
import {Badge,Modal,FormInput,fmt} from '../components/common';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import {ShoppingCart,Plus,Minus,X,RotateCcw,Percent,ArrowLeft,User,
  DollarSign,LogOut,Search,Package,Layers,CreditCard,CheckCircle,
  Receipt,RefreshCw,Clock,ChevronRight,Ban,Building2,Lock,
  AlertTriangle,LogIn,AlertCircle,ShieldAlert,Warehouse,Printer,
  Maximize2,Minimize2,StickyNote,TrendingUp,Info,Home,LayoutDashboard,History} from 'lucide-react';
import toast from 'react-hot-toast';

const KHR_RATE=4100;
function useIsMobile(){const[m,setM]=useState(()=>window.innerWidth<768);useEffect(()=>{const h=()=>setM(window.innerWidth<768);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h)},[]);return m;}
const fmtTime=d=>d?new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}):'—';
const fmtFull=d=>d?new Date(d).toLocaleString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'2-digit',minute:'2-digit'}):'—';
const METHOD_COLORS={Cash:'#22c55e',Card:'#6366f1',ABA:'#3b82f6',Wing:'#f59e0b','Pi Pay':'#ec4899',KHQR:'#8b5cf6'};
const getMethodColor=n=>METHOD_COLORS[n]||`hsl(${(n||'').charCodeAt(0)*37%360},60%,55%)`;
const TxnPill=({t})=>{const v=t.status==='voided',r=t.type==='refund';return<span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 9px',borderRadius:20,fontSize:10,fontWeight:700,background:v?'var(--dm)':r?'var(--wm)':'var(--sm)',color:v?'var(--danger)':r?'var(--warning)':'var(--success)',whiteSpace:'nowrap'}}>{v?'VOIDED':r?'REFUND':'SALE'}</span>};
const MethodPills=({payments=[]})=><div style={{display:'flex',gap:4,flexWrap:'wrap'}}>{payments.map((p,i)=>{const n=p.method?.name||'—',c=getMethodColor(n);return<span key={i} style={{fontSize:10,fontWeight:700,color:c,background:c+'22',padding:'2px 7px',borderRadius:10,whiteSpace:'nowrap'}}>{n} {fmt(p.amount)}</span>})}</div>;
function StockBadge({qty,allowNeg}){if(qty===null)return null;const out=qty<=0&&!allowNeg,low=qty>0&&qty<=10;return<div style={{position:'absolute',top:4,right:4,padding:'2px 6px',borderRadius:4,fontSize:9,fontWeight:700,background:out?'var(--danger)':low?'var(--warning)':'var(--success)',color:'#fff',display:'flex',alignItems:'center',gap:3}}><Warehouse size={7}/>{out?'OUT':low?`LOW:${qty}`:qty}</div>}

/* ══════════════ PRINT RECEIPT MODAL ══════════════ */
function ReceiptModal({txn,business,branch,onClose}){
  const{t}=useLang();
  if(!txn)return null;
  const discount=parseFloat(txn.discount_amount||0);
  const change=parseFloat(txn.change_amount||0);
  const khrRate=txn.exchange_rate||4100;
  const fmtKHR=n=>`≈ ៛${Math.round((parseFloat(n)||0)*khrRate).toLocaleString()}`;

  // Open a new window, write receipt HTML, print it, close it
  const handlePrint=()=>{
    const el=document.getElementById('receipt-print-area');
    if(!el){return;}
    const html=`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Receipt</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Noto+Serif+Khmer:wght@400;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{
    font-family:'Noto Serif Khmer','Khmer OS',monospace,Arial,sans-serif;
    font-size:12px;color:#000;background:#fff;
    width:80mm;padding:8mm 6mm;
  }
  @page{margin:0;size:80mm auto;}
  @media print{body{width:80mm;}}
  table{width:100%;border-collapse:collapse;}
  .dashed{border-top:1px dashed #999;margin:6px 0;}
  .dashed2{border-top:2px dashed #000;margin:8px 0;}
</style>
</head>
<body>${el.innerHTML}</body>
</html>`;
    const pw=window.open('','_blank','width=400,height=600,scrollbars=yes');
    if(!pw){alert('Please allow popups to print');return;}
    pw.document.write(html);
    pw.document.close();
    pw.focus();
    setTimeout(()=>{pw.print();pw.close();},600);
  };

  const R={borderTop:'1px dashed #999',margin:'6px 0'};
  const Row=({kh,en,val,bold})=>(
    <div style={{display:'flex',justifyContent:'space-between',fontSize:11,margin:'1px 0'}}>
      <span style={{color:'#555'}}><span style={{fontSize:12}}>{kh}</span> / <span style={{fontSize:10}}>{en}</span></span>
      <span style={{fontWeight:bold?700:400}}>{val}</span>
    </div>
  );

  return(
    <Modal open onClose={onClose} title={`${t('pos.receipt')}`} width={440}>

      {/* ── RECEIPT CONTENT (shown on screen + printed) ── */}
      <div id="receipt-print-area" style={{
        fontFamily:"'Noto Serif Khmer','Khmer OS',monospace,sans-serif",
        fontSize:12,lineHeight:1.6,background:'#fff',color:'#000',padding:'4px 2px'
      }}>

        {/* Header */}
        {business?.logo_url&&<div style={{textAlign:'center',marginBottom:6}}>
          <img src={business.logo_url} alt="logo" style={{height:48,objectFit:'contain'}}/>
        </div>}
        <div style={{textAlign:'center',marginBottom:6}}>
          <div style={{fontSize:15,fontWeight:900}}>{branch?.name||business?.name||'Receipt'}</div>
          {branch?.address&&<div style={{fontSize:10,color:'#555'}}>{branch.address}{branch.city?`, ${branch.city}`:''}</div>}
          {branch?.phone&&<div style={{fontSize:10,color:'#555'}}>
            <span style={{fontSize:11}}>ទូរស័ព្ទ / Tel:</span> {branch.phone}
          </div>}
          {(business?.tax_id||branch?.tax_id)&&<div style={{fontSize:10,color:'#555'}}>
            <span style={{fontSize:11}}>លេខអត្តសញ្ញាណពន្ធ / TIN:</span> {business?.tax_id||branch?.tax_id}
          </div>}
        </div>

        <div style={R}/>

        {/* Transaction meta */}
        <Row kh="វិក្កយបត្រ" en="Receipt #" val={txn.transaction_number} bold/>
        <Row kh="កាលបរិច្ឆេទ" en="Date" val={fmtFull(txn.createdAt||txn.created_at)}/>
        <Row kh="អ្នកគិតលុយ" en="Cashier" val={txn.cashier?`${txn.cashier.first_name} ${txn.cashier.last_name}`:(txn.cashier_name||'—')}/>
        {txn.customer?.name&&<Row kh="អតិថិជន" en="Customer" val={txn.customer.name}/>}
        {txn.table_number&&<Row kh="តុ" en="Table" val={txn.table_number}/>}

        <div style={R}/>

        {/* Items */}
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead>
            <tr style={{borderBottom:'1px solid #000'}}>
              <th style={{textAlign:'left',paddingBottom:3,fontWeight:700,fontSize:10}}>
                <div>មុខទំនិញ</div><div style={{fontWeight:400}}>Item</div>
              </th>
              <th style={{textAlign:'right',paddingBottom:3,fontWeight:700,fontSize:10,width:38}}>
                <div>ចំនួន</div><div style={{fontWeight:400}}>Qty</div>
              </th>
              <th style={{textAlign:'right',paddingBottom:3,fontWeight:700,fontSize:10,width:48}}>
                <div>តម្លៃ</div><div style={{fontWeight:400}}>Price</div>
              </th>
              <th style={{textAlign:'right',paddingBottom:3,fontWeight:700,fontSize:10,width:48}}>
                <div>សរុប</div><div style={{fontWeight:400}}>Total</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {(txn.items||[]).map((item,i)=>{
              const uomLabel=item.uom?.abbreviation||item.uom?.name||'';
              const disc=parseFloat(item.discount_amount||0);
              return(
                <tr key={i} style={{borderBottom:'1px solid #eee'}}>
                  <td style={{paddingTop:4,paddingBottom:3}}>
                    <div style={{fontWeight:700,fontSize:12}}>{item.product?.name_kh||item.product?.name||'—'}</div>
                    {item.product?.name_kh&&<div style={{fontSize:9,color:'#777'}}>{item.product.name}</div>}
                    {item.product?.sku&&<div style={{fontSize:9,color:'#aaa'}}>{item.product.sku}{uomLabel?` · ${uomLabel}`:''}</div>}
                    {disc>0&&<div style={{fontSize:9,color:'#c00'}}>Disc: -{fmt(disc)}</div>}
                  </td>
                  <td style={{textAlign:'right',paddingTop:4,verticalAlign:'top',fontSize:11}}>
                    {item.quantity}{uomLabel?<span style={{fontSize:9,color:'#888'}}> {uomLabel}</span>:''}
                  </td>
                  <td style={{textAlign:'right',paddingTop:4,verticalAlign:'top',fontSize:11}}>{fmt(item.unit_price)}</td>
                  <td style={{textAlign:'right',paddingTop:4,verticalAlign:'top',fontSize:11,fontWeight:700}}>{fmt(item.line_total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={R}/>

        {/* Totals */}
        <div style={{display:'flex',justifyContent:'space-between',fontSize:11}}>
          <span><span style={{fontSize:12}}>សរុបរង</span> / Subtotal</span>
          <span>{fmt(txn.subtotal)}</span>
        </div>
        {discount>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#c00'}}>
          <span><span style={{fontSize:12}}>បញ្ចុះតម្លៃ</span> / Discount</span>
          <span>-{fmt(discount)}</span>
        </div>}
        {parseFloat(txn.tax_amount||0)>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:11}}>
          <span><span style={{fontSize:12}}>អាករ</span> / Tax</span>
          <span>{fmt(txn.tax_amount)}</span>
        </div>}

        <div style={{display:'flex',justifyContent:'space-between',fontSize:15,fontWeight:900,margin:'5px 0 2px'}}>
          <span><span style={{fontSize:14}}>សរុបរួម</span> / TOTAL</span>
          <span style={{textAlign:'right'}}>
            <div>{fmt(txn.total_amount)}</div>
            <div style={{fontSize:10,fontWeight:400,color:'#888'}}>{fmtKHR(txn.total_amount)}</div>
          </span>
        </div>

        <div style={R}/>

        {/* Payments */}
        <div style={{fontSize:11,marginBottom:3,fontWeight:700}}>
          <span style={{fontSize:12}}>វិធីបង់ប្រាក់</span> / Payment Method
        </div>
        {(txn.payments||[]).map((p,i)=>{
          const n=p.method?.name||'—';
          const origCur=p.original_currency||p.currency||'USD';
          const origAmt=p.original_amount!=null?parseFloat(p.original_amount):parseFloat(p.amount);
          const isKHR=origCur==='KHR';
          return(
            <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:11,margin:'1px 0'}}>
              <span>{n}</span>
              <span>
                {isKHR?(
                  <><strong>៛{Math.round(origAmt).toLocaleString()}</strong>
                  <span style={{fontSize:9,color:'#888',marginLeft:3}}>(≈ {fmt(parseFloat(p.amount))})</span></>
                ):(
                  <strong>{fmt(origAmt)}</strong>
                )}
              </span>
            </div>
          );
        })}

        {change>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:12,fontWeight:700,color:'#2e7d32',marginTop:3}}>
          <span><span style={{fontSize:13}}>ប្រាក់អាប់</span> / Change</span>
          <span style={{textAlign:'right'}}>
            <div>{fmt(change)}</div>
            <div style={{fontSize:9,fontWeight:400,color:'#888'}}>
              {`≈ ៛${Math.round(change*khrRate).toLocaleString()}`}
            </div>
          </span>
        </div>}

        <div style={{borderTop:'2px dashed #000',margin:'8px 0'}}/>

        {/* Footer */}
        <div style={{textAlign:'center',fontSize:12,fontWeight:700,marginBottom:2}}>
          🙏 សូមអរគុណ! សូមមកម្តងទៀត
        </div>
        <div style={{textAlign:'center',fontSize:10,color:'#777',marginBottom:4}}>
          Thank you! Please come again
        </div>
        <div style={{textAlign:'center',fontSize:9,color:'#aaa'}}>
          មិនអាចប្ដូរឬសងប្រាក់វិញបាន / No refund
        </div>
        {business?.website&&<div style={{textAlign:'center',fontSize:9,color:'#aaa',marginTop:2}}>
          {business.website}
        </div>}

      </div>
      {/* ── ACTION BUTTONS (hidden when printing) ── */}
      <div className="modal-footer" style={{gap:8}} id="receipt-no-print">
        <button className="btn btn-ghost" onClick={onClose}>{t('common.close')}</button>
        <button className="btn btn-primary" onClick={handlePrint} style={{gap:6,display:'flex',alignItems:'center'}}>
          <Printer size={15}/> {t('common.print')||'Print'}
        </button>
      </div>
    </Modal>
  );
}


/* ══════════════ ORDERS TAB ══════════════ */
function OrdersTab({txnHistory,loadHistory,loading,isMobile,fmtKHR,getKhrRate,canVoid,branchName,business,branch}){
  const[search,setSearch]=useState('');
  const[typeFilter,setTypeFilter]=useState('all');
  const[selected,setSelected]=useState(null);
  const[voiding,setVoiding]=useState(false);
  const[voidConfirm,setVoidConfirm]=useState(false);
  const[showDetail,setShowDetail]=useState(false);
  const[printTxn,setPrintTxn]=useState(null);

  const filtered=txnHistory.filter(t=>{
    const q=search.trim().toLowerCase();
    if(q&&!((t.transaction_number||'').toLowerCase().includes(q)||(t.customer?.name||'').toLowerCase().includes(q)||(t.items||[]).some(i=>(i.product?.name||'').toLowerCase().includes(q))))return false;
    if(typeFilter==='sale'&&(t.type!=='sale'||t.status==='voided'))return false;
    if(typeFilter==='refund'&&t.type!=='refund')return false;
    if(typeFilter==='voided'&&t.status!=='voided')return false;
    return true;
  });

  const totalSales=txnHistory.filter(t=>t.type==='sale'&&t.status!=='voided').reduce((s,t)=>s+parseFloat(t.total_amount||0),0);
  const totalCount=txnHistory.filter(t=>t.type==='sale'&&t.status!=='voided').length;

  const doVoid=async()=>{if(!selected)return;setVoiding(true);try{await posAPI.voidTransaction(selected.id);toast.success(`${selected.transaction_number} voided`);setVoidConfirm(false);setSelected(s=>s?{...s,status:'voided'}:null);loadHistory();}catch(e){toast.error(e.response?.data?.error||'Void failed')}finally{setVoiding(false)}};

  const DetailPanel=({txn,onClose})=>{
    if(!txn)return<div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'var(--td)',gap:12,background:'var(--s2)'}}><Receipt size={40} style={{opacity:.2}}/><div style={{fontSize:13}}>Select an order to view details</div></div>;
    const voided=txn.status==='voided';const discount=parseFloat(txn.discount_amount||0);const change=parseFloat(txn.change_amount||0);
    return(
      <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
        <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10,flexShrink:0,background:'var(--surface)'}}>
          {isMobile&&<button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',padding:0,display:'flex',flexShrink:0}}><ArrowLeft size={20}/></button>}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:800,fontFamily:'monospace',color:'var(--primary)'}}>{txn.transaction_number}</div>
            <div style={{fontSize:11,color:'var(--td)',marginTop:1}}>{fmtFull(txn.created_at)}</div>
          </div>
          <TxnPill t={txn}/>
          {!voided&&<button className="btn btn-ghost btn-sm" onClick={()=>setPrintTxn(txn)} style={{gap:4,color:'var(--primary)'}}><Printer size={13}/>{!isMobile&&' Print'}</button>}
          {txn.type==='sale'&&!voided&&canVoid&&(voidConfirm?
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <span style={{fontSize:11,color:'var(--danger)',fontWeight:600}}>Void?</span>
              <button className="btn btn-danger btn-sm" onClick={doVoid} disabled={voiding}>{voiding?<RefreshCw size={11} style={{animation:'spin 1s linear infinite'}}/>:'Yes'}</button>
              <button className="btn btn-ghost btn-sm" onClick={()=>setVoidConfirm(false)}>No</button>
            </div>:
            <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)',gap:4}} onClick={()=>setVoidConfirm(true)}><Ban size={13}/>{!isMobile&&' Void'}</button>
          )}
          {!isMobile&&<button onClick={()=>setSelected(null)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',padding:4,display:'flex'}}><X size={16}/></button>}
        </div>
        {voided&&<div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',background:'var(--dm)',borderBottom:'1px solid rgba(239,68,68,.2)'}}><Ban size={14} color="var(--danger)"/><span style={{fontSize:12,color:'var(--danger)',fontWeight:700}}>Voided — stock was restored to {branchName||'branch'}.</span></div>}
        <div style={{flex:1,overflowY:'auto',padding:'16px 18px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
            <div style={{background:'var(--s2)',borderRadius:10,padding:'11px 13px'}}><div style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Customer</div><div style={{fontSize:13,fontWeight:700}}>{txn.customer?.name||'Walk-in'}</div>{txn.customer?.phone&&<div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{txn.customer.phone}</div>}</div>
            <div style={{background:'var(--s2)',borderRadius:10,padding:'11px 13px'}}><div style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Cashier · Branch</div><div style={{fontSize:13,fontWeight:700}}>{txn.cashier?`${txn.cashier.first_name} ${txn.cashier.last_name}`:'—'}</div><div style={{fontSize:11,color:'var(--primary)',marginTop:2,display:'flex',alignItems:'center',gap:4}}><Building2 size={10}/>{branchName||'—'}</div></div>
          </div>
          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden',marginBottom:14}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr style={{background:'var(--s2)'}}>{['Product','UoM','Qty','Price','Total'].map((h,i)=><th key={h} style={{padding:'8px 10px',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:.4,color:'var(--td)',textAlign:i>=2?'right':'left',borderBottom:'1px solid var(--border)'}}>{h}</th>)}</tr></thead>
              <tbody>
                {(txn.items||[]).map((item,i)=>(
                  <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'9px 10px'}}><div style={{fontSize:13,fontWeight:600}}>{item.product?.name||'—'}</div>{item.product?.sku&&<div style={{fontSize:10,color:'var(--td)'}}>{item.product.sku}</div>}{parseFloat(item.discount_amount||0)>0&&<div style={{fontSize:10,color:'var(--warning)'}}>Disc: -{fmt(item.discount_amount)}</div>}</td>
                    <td style={{padding:'9px 10px',fontSize:12,color:'var(--td)'}}>{item.uom?.abbreviation||item.uom?.name||'—'}</td>
                    <td style={{padding:'9px 10px',textAlign:'right',fontSize:13,fontWeight:600}}>{item.quantity}</td>
                    <td style={{padding:'9px 10px',textAlign:'right',fontSize:13}}>{fmt(item.unit_price)}</td>
                    <td style={{padding:'9px 10px',textAlign:'right',fontSize:13,fontWeight:800,color:'var(--primary)'}}>{fmt(item.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{background:'var(--s2)',borderRadius:10,padding:'13px 15px',marginBottom:14}}>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}><span style={{color:'var(--td)'}}>Subtotal</span><span style={{fontWeight:600}}>{fmt(txn.subtotal)}</span></div>
            {discount>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}><span style={{color:'var(--warning)'}}>Discount</span><span style={{color:'var(--warning)',fontWeight:600}}>-{fmt(discount)}</span></div>}
            <div style={{display:'flex',justifyContent:'space-between',borderTop:'2px solid var(--border)',paddingTop:10,fontSize:18,fontWeight:900}}><span>TOTAL</span><span style={{color:'var(--primary)'}}>{fmt(txn.total_amount)}</span></div>
            <div style={{fontSize:12,color:'var(--td)',textAlign:'right',marginTop:2}}>{fmtKHR(parseFloat(txn.total_amount||0)*getKhrRate())}</div>
          </div>
          {(txn.payments||[]).length>0&&<div style={{marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5,marginBottom:8}}>Payment</div>
            {(txn.payments||[]).map((p,i)=>{const n=p.method?.name||'—',c=getMethodColor(n);return<div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',background:'var(--s2)',borderRadius:9,marginBottom:5,border:`1px solid ${c}33`}}><div style={{width:8,height:8,borderRadius:'50%',background:c,flexShrink:0}}/><span style={{flex:1,fontSize:13,fontWeight:700,color:c}}>{n}</span><span style={{fontSize:14,fontWeight:800}}>{fmt(p.amount)}</span></div>})}
            {change>0&&<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 12px',background:'var(--am)',borderRadius:9,marginTop:5}}><span style={{fontSize:13,fontWeight:700,color:'var(--accent)'}}>Change</span><div style={{textAlign:'right'}}><div style={{fontSize:14,fontWeight:800,color:'var(--accent)'}}>{fmt(change)}</div><div style={{fontSize:11,color:'var(--accent)'}}>{fmtKHR(change*getKhrRate())}</div></div></div>}
          </div>}
        </div>
      </div>
    );
  };

  const TxnCard=({t})=>{const isActive=selected?.id===t.id,voided=t.status==='voided';return(
    <div onClick={()=>{setSelected(t);setVoidConfirm(false);if(isMobile)setShowDetail(true)}} style={{padding:'12px 14px',borderRadius:10,marginBottom:4,cursor:'pointer',border:`1.5px solid ${isActive?'var(--primary)':voided?'rgba(239,68,68,.2)':'var(--border)'}`,background:isActive?'var(--pm)':voided?'var(--dm)':'var(--surface)',opacity:voided?.75:1,transition:'all .12s'}} onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=voided?'var(--dm)':'var(--s2)'}} onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background=voided?'var(--dm)':'var(--surface)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}><span style={{fontSize:12,fontWeight:800,color:isActive?'var(--primary)':'var(--accent)',fontFamily:'monospace',flex:1}}>{t.transaction_number}</span><TxnPill t={t}/>{isActive&&!isMobile&&<ChevronRight size={13} color="var(--primary)"/>}</div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}><div style={{display:'flex',alignItems:'center',gap:5}}><User size={11} color="var(--td)"/><span style={{fontSize:12,color:'var(--td)',fontWeight:500}}>{t.customer?.name||'Walk-in'}</span></div><div style={{display:'flex',alignItems:'center',gap:4}}><Clock size={11} color="var(--td)"/><span style={{fontSize:11,color:'var(--td)'}}>{fmtTime(t.created_at)}</span></div></div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}><MethodPills payments={t.payments||[]}/><span style={{fontSize:14,fontWeight:800,color:voided?'var(--td)':isActive?'var(--primary)':'var(--text)',textDecoration:voided?'line-through':'none',flexShrink:0,marginLeft:8}}>{fmt(parseFloat(t.total_amount||0))}</span></div>
    </div>
  )};

  const SummaryStrip=<div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',borderBottom:'1px solid var(--border)',flexShrink:0}}>{[{label:'Sales',val:fmt(totalSales),color:'var(--primary)'},{label:'Transactions',val:totalCount,color:'var(--text)'},{label:'Avg',val:totalCount?fmt(totalSales/totalCount):'—',color:'var(--td)'}].map(({label,val,color})=><div key={label} style={{padding:'8px 12px',borderRight:'1px solid var(--border)'}}><div style={{fontSize:9,color:'var(--td)',fontWeight:700,textTransform:'uppercase',letterSpacing:.4,marginBottom:2}}>{label}</div><div style={{fontSize:13,fontWeight:800,color}}>{val}</div></div>)}</div>;
  const FilterBar=<div style={{padding:'10px 12px',borderBottom:'1px solid var(--border)',flexShrink:0}}><div style={{position:'relative',marginBottom:8}}><Search size={13} style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'var(--td)'}}/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search # / customer / product..." style={{width:'100%',padding:'7px 10px 7px 30px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12,boxSizing:'border-box'}}/></div><div style={{display:'flex',gap:4,alignItems:'center'}}>{['all','sale','refund','voided'].map(f=><button key={f} onClick={()=>setTypeFilter(f)} style={{padding:'4px 10px',borderRadius:20,border:'1px solid var(--border)',fontSize:11,fontWeight:700,cursor:'pointer',background:typeFilter===f?'var(--primary)':'var(--surface)',color:typeFilter===f?'#fff':'var(--td)'}}>{f==='all'?'All':f[0].toUpperCase()+f.slice(1)}</button>)}<button onClick={loadHistory} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'var(--td)',display:'flex',alignItems:'center',padding:'4px 6px',borderRadius:6}}><RefreshCw size={12}/></button></div></div>;
  const ListContent=<div style={{flex:1,overflowY:'auto',padding:'6px 8px'}}>{loading?<div style={{padding:40,textAlign:'center',color:'var(--td)'}}><RefreshCw size={16} style={{animation:'spin 1s linear infinite'}}/></div>:filtered.length===0?<div style={{padding:40,textAlign:'center',color:'var(--td)',fontSize:13}}><Receipt size={28} style={{opacity:.2,display:'block',margin:'0 auto 10px'}}/>No transactions</div>:filtered.map(t=><TxnCard key={t.id} t={t}/>)}</div>;

  if(isMobile){
    if(showDetail&&selected)return<div style={{height:'100%',display:'flex',flexDirection:'column',overflow:'hidden'}}><DetailPanel txn={selected} onClose={()=>{setShowDetail(false);setVoidConfirm(false)}}/></div>;
    return<div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>{FilterBar}{SummaryStrip}{ListContent}</div>;
  }
  return(
    <div style={{display:'flex',height:'100%',overflow:'hidden'}}>
      <div style={{width:400,flexShrink:0,borderRight:'1px solid var(--border)',display:'flex',flexDirection:'column',overflow:'hidden'}}>{FilterBar}{SummaryStrip}{ListContent}</div>
      <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column',background:'var(--surface)'}}><DetailPanel txn={selected}/></div>
      {printTxn&&<ReceiptModal txn={printTxn} business={business} branch={branch} onClose={()=>setPrintTxn(null)}/>}
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN POS PAGE
══════════════════════════════════════════════ */
export default function PosPage(){
  const isMobile=useIsMobile();
  const navigate=useNavigate();
  const{t}=useLang();
  const{user,hasPermission,isOwner,myBranchId,myBranch}=useAuth();

  const canAccess=hasPermission('pos.access');
  const canOpenSession=hasPermission('pos.open_session')||hasPermission('register.open')||isOwner;
  const canCloseSession=hasPermission('pos.close_session')||hasPermission('pos.close_register')||hasPermission('register.close')||isOwner;
  const canDiscount=hasPermission('pos.discount')||isOwner;
  const canVoid=hasPermission('pos.refund')||isOwner;

  const[session,setSession]=useState(null);
  const[products,setProducts]=useState([]);
  const[categories,setCategories]=useState([]);
  const[customers,setCustomers]=useState([]);
  const[methods,setMethods]=useState([]);
  const[inventory,setInventory]=useState([]);
  const[inventoryBranchId,setInventoryBranchId]=useState(null);
  const[rates,setRates]=useState([]);
  const[txnHistory,setTxnHistory]=useState([]);
  const[txnLoading,setTxnLoading]=useState(false);
  const[business,setBusiness]=useState(null);
  const[cart,setCart]=useState([]);
  const[search,setSearch]=useState('');
  const[catFilter,setCatFilter]=useState('all');
  const[custId,setCustId]=useState('');
  const[cartDiscount,setCartDiscount]=useState(0);
  const[cartDiscountType,setCartDiscountType]=useState('amount');
  const[tab,setTab]=useState('products');
  const[modal,setModal]=useState(null);
  const[payModal,setPayModal]=useState(false);
  const[payLines,setPayLines]=useState([]);
  const[openBal,setOpenBal]=useState('0');
  const[closeBal,setCloseBal]=useState('');
  const[closeNotes,setCloseNotes]=useState('');
  const[isFullscreen,setIsFullscreen]=useState(false);
  const[stockErrors,setStockErrors]=useState([]);
  const[validating,setValidating]=useState(false);
  const[printTxn,setPrintTxn]=useState(null);  // receipt to print after checkout
  const[lastTxn,setLastTxn]=useState(null);     // last completed transaction
  const searchRef=useRef(null);

  // Sidebar/topbar hiding is handled by AppLayout based on route path (/pos)
  // No DOM manipulation needed here

  // Auto-focus search whenever products tab is active and session is open
  useEffect(()=>{
    if(tab==='products' && session){
      const timer=setTimeout(()=>searchRef.current?.focus(),100);
      return()=>clearTimeout(timer);
    }
  },[tab,session]);

  const activeBranchId=session?.branch_id||(!isOwner?myBranchId:null);
  const activeBranchName=session?.branch?.name||myBranch?.name||null;

  // Selected customer object (for discount)
  const selectedCustomer=useMemo(()=>customers.find(c=>String(c.id)===String(custId))||null,[customers,custId]);

  // When customer selected, auto-apply their default discount
  useEffect(()=>{
    if(!canDiscount)return;
    if(selectedCustomer?.discount_percent>0){
      setCartDiscountType('percent');
      setCartDiscount(parseFloat(selectedCustomer.discount_percent));
    } else if(selectedCustomer&&parseFloat(selectedCustomer?.discount_percent||0)===0){
      // Customer has no discount — reset to 0 but keep user's manual changes
      // Only reset if it was from a previous customer auto-fill
      setCartDiscount(prev=>{
        // Don't clear if user manually changed it
        if(prev===0)return 0;
        return prev;
      });
    }
  },[selectedCustomer,canDiscount]);

  useEffect(()=>{
    Promise.all([
      productAPI.getAll({limit:500,is_active:true}),
      customerAPI.getAll({limit:200}),
      paymentAPI.getMethods(),
      categoryAPI.getAll({limit:100}),
      currencyAPI.getRates(),
      businessAPI.get(),
    ]).then(([p,c,m,cat,r,biz])=>{
      setProducts(p.data.data||[]);
      setCustomers(c.data.data||[]);
      setMethods((m.data.data||m.data).filter(x=>x.is_active));
      setCategories(cat.data.data||cat.data);
      setRates(r.data.data||r.data);
      setBusiness(biz.data||null);
    }).catch(()=>{});
    if(canAccess||canOpenSession){
      posAPI.getSession().then(r=>setSession(r.data)).catch(()=>{});
    }
  },[canAccess,canOpenSession]);

  const loadInventory=useCallback((branchId)=>{
    if(!branchId)return;
    inventoryAPI.getAll({branch_id:branchId,limit:1000}).then(r=>{setInventory(r.data.data||[]);setInventoryBranchId(branchId);}).catch(()=>{});
  },[]);

  useEffect(()=>{const bid=session?.branch_id||activeBranchId;if(bid)loadInventory(bid);},[session,activeBranchId,loadInventory]);

  const loadHistory=useCallback(()=>{if(!session)return;setTxnLoading(true);posAPI.getTransactions({session_id:session.id,limit:100}).then(r=>setTxnHistory(r.data.data||[])).catch(()=>{}).finally(()=>setTxnLoading(false));},[session]);
  useEffect(()=>{if(tab==='orders'&&session)loadHistory()},[tab,session,loadHistory]);

  const getKhrRate=()=>{const r=rates.find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');return r?parseFloat(r.rate):KHR_RATE};
  const fmtKHR=n=>`៛${Math.round(n).toLocaleString()}`;

  const getStock=useCallback((pid)=>{
    if(inventoryBranchId&&activeBranchId&&inventoryBranchId!==activeBranchId)return null;
    const inv=inventory.find(i=>i.product_id===pid);
    return inv?parseFloat(inv.quantity):0;
  },[inventory,inventoryBranchId,activeBranchId]);

  const invMap=useMemo(()=>{const m={};inventory.forEach(i=>{m[i.product_id]=parseFloat(i.quantity)});return m;},[inventory]);

  const cartStockWarnings=useMemo(()=>cart.filter(item=>{
    const avail=invMap[item.product_id];
    const prod=products.find(p=>p.id===item.product_id);
    if(prod?.allow_negative_stock)return false;
    if(avail===undefined)return false;
    // Account for UoM conversion: check base units needed
    const factor=item.uom_conversion_factor||1;
    const baseNeeded=item.quantity*factor;
    return baseNeeded>(avail??0);
  }).map(item=>({product_id:item.product_id,name:item.name,needed:item.quantity*(item.uom_conversion_factor||1),available:invMap[item.product_id]??0})),[cart,invMap,products]);

  const filtered=products.filter(p=>{const q=search.toLowerCase();return(!search||p.name.toLowerCase().includes(q)||p.sku.toLowerCase().includes(q)||(p.barcode||'').includes(search))&&(catFilter==='all'||p.category_id===parseInt(catFilter))});

  const addToCart=p=>{
    const stock=getStock(p.id);
    if(!p.allow_negative_stock&&stock!==null&&stock<=0){toast.error(`"${p.name}" is out of stock at ${activeBranchName||'this branch'}`);return;}
    setCart(c=>{
      const ex=c.find(i=>i.product_id===p.id&&i.uom_id===p.uom_id);
      if(ex){
        if(!p.allow_negative_stock&&stock!==null&&(ex.quantity+1)*(ex.uom_conversion_factor||1)>stock){toast.error(`Only ${stock} base units of "${p.name}" in stock`);return c;}
        return c.map(i=>i.product_id===p.id&&i.uom_id===p.uom_id?{...i,quantity:i.quantity+1}:i);
      }
      // Build UoM options list: base UoM first, then alternatives
      const baseUomLabel=p.uom?.abbreviation||p.uom?.name||'';
      const altUoms=(p.uoms||[]).map(pu=>({uom_id:pu.uom_id,label:pu.uom?.abbreviation||pu.uom?.name||`UoM ${pu.uom_id}`,factor:parseFloat(pu.conversion_factor)||1,price:pu.price?parseFloat(pu.price):null}));
      return[...c,{
        product_id:p.id,name:p.name,sku:p.sku,image_url:p.image_url,
        allow_negative_stock:p.allow_negative_stock||false,
        quantity:1,unit_price:parseFloat(p.sale_price),
        discount_amount:0,discount_pct:0,
        // UoM fields
        uom_id:p.uom_id||null,
        uom_label:baseUomLabel,
        uom_conversion_factor:1,
        base_price:parseFloat(p.sale_price),
        alt_uoms:altUoms,   // available alternatives
      }];
    });
    if(isMobile)toast.success(`Added ${p.name}`,{duration:900,icon:'🛒'});
  };

  const changeItemUom=(idx,uomId)=>{
    setCart(c=>c.map((item,i)=>{
      if(i!==idx)return item;
      if(!uomId||uomId==='base'){
        // Switch back to base UoM
        return{...item,uom_id:products.find(p=>p.id===item.product_id)?.uom_id||null,uom_label:products.find(p=>p.id===item.product_id)?.uom?.abbreviation||'',uom_conversion_factor:1,unit_price:item.base_price,discount_amount:0,discount_pct:0};
      }
      const alt=item.alt_uoms?.find(u=>String(u.uom_id)===String(uomId));
      if(!alt)return item;
      const newPrice=alt.price||+(item.base_price*alt.factor).toFixed(2);
      return{...item,uom_id:alt.uom_id,uom_label:alt.label,uom_conversion_factor:alt.factor,unit_price:newPrice,discount_amount:0,discount_pct:0};
    }));
  };

  const updateQty=(idx,q)=>{
    const item=cart[idx];
    const stock=getStock(item.product_id);
    if(q<1){removeItem(idx);return}
    const factor=item.uom_conversion_factor||1;
    if(!item.allow_negative_stock&&stock!==null&&q*factor>stock){toast.error(`Only ${Math.floor(stock/factor)} ${item.uom_label||'units'} available`);return;}
    setCart(c=>c.map((i,j)=>j===idx?{...i,quantity:Math.max(1,q)}:i));
  };
  const updateItemDiscount=(idx,val,type)=>setCart(c=>c.map((i,j)=>{if(j!==idx)return i;if(type==='pct'){const pct=parseFloat(val)||0;return{...i,discount_pct:pct,discount_amount:+(i.unit_price*i.quantity*pct/100).toFixed(2)}}return{...i,discount_amount:parseFloat(val)||0,discount_pct:0}}));
  const removeItem=idx=>setCart(c=>c.filter((_,j)=>j!==idx));
  const clearCart=()=>{setCart([]);setCartDiscount(0);setCustId('');setStockErrors([]);setLastTxn(null)};

  const subtotal=cart.reduce((s,i)=>s+(i.quantity*i.unit_price),0);
  const itemDiscounts=cart.reduce((s,i)=>s+(i.discount_amount||0),0);
  const globalDiscount=canDiscount?(cartDiscountType==='percent'?+(subtotal*parseFloat(cartDiscount||0)/100).toFixed(2):parseFloat(cartDiscount||0)):0;
  const totalDiscount=itemDiscounts+globalDiscount;
  const total=Math.max(0,subtotal-totalDiscount);
  const totalKHR=Math.round(total*getKhrRate());
  const cartCount=cart.reduce((s,i)=>s+i.quantity,0);

  const openPayment=async()=>{
    if(!cart.length)return;
    setValidating(true);setStockErrors([]);
    try{
      const items=cart.map(i=>({product_id:i.product_id,quantity:i.quantity,uom_id:i.uom_id||null}));
      const r=await posAPI.validateStock(items);
      if(!r.data.valid){setStockErrors(r.data.errors||[]);toast.error(`Stock issue at ${activeBranchName||'this branch'}`);setValidating(false);return;}
      if(activeBranchId){const inv=await inventoryAPI.getAll({branch_id:activeBranchId,limit:1000});setInventory(inv.data.data||[]);setInventoryBranchId(activeBranchId);}
    }catch{}
    setValidating(false);
    setPayLines([{method_id:methods[0]?.id||'',currency:'USD',amount:String(total.toFixed(2))}]);
    setPayModal(true);
  };

  const addPayLine=()=>setPayLines(l=>[...l,{method_id:methods[0]?.id||'',currency:'USD',amount:''}]);
  const updatePayLine=(idx,key,val)=>setPayLines(l=>l.map((p,i)=>i===idx?{...p,[key]:val}:p));
  const removePayLine=idx=>setPayLines(l=>l.filter((_,i)=>i!==idx));
  const paidUSD=payLines.reduce((s,p)=>{const amt=parseFloat(p.amount)||0;return s+(p.currency==='KHR'?amt/getKhrRate():amt)},0);
  const changeUSD=Math.max(0,paidUSD-total);

  const checkout=async()=>{
    if(!cart.length)return toast.error('Cart empty');
    if(paidUSD<total-0.01)return toast.error('Insufficient payment');
    try{
      const khrRate=getKhrRate();
      const payments=payLines.filter(p=>parseFloat(p.amount)>0).map(p=>({
        payment_method_id:parseInt(p.method_id)||methods[0]?.id,
        amount:p.currency==='KHR'?+((parseFloat(p.amount)/khrRate).toFixed(2)):parseFloat(p.amount),
        original_amount:parseFloat(p.amount),
        original_currency:p.currency,
      }));
      const result=await posAPI.createTransaction({
        items:cart.map(i=>({product_id:i.product_id,quantity:i.quantity,unit_price:i.unit_price,discount_amount:i.discount_amount||0,uom_id:i.uom_id||null,uom_conversion_factor:i.uom_conversion_factor||1})),
        customer_id:custId?parseInt(custId):null,
        discount_amount:globalDiscount,
        exchange_rate:khrRate,
        payments,
      });
      const txn=result.data;
      setLastTxn(txn);
      toast.success(`✅ Sale ${txn.transaction_number} completed!`);
      clearCart();setPayModal(false);setStockErrors([]);
      if(isMobile)setTab('products');
      if(activeBranchId)loadInventory(activeBranchId);
      if(tab==='orders')loadHistory();
      // Auto-show receipt
      setPrintTxn(txn);
    }catch(e){
      const err=e.response?.data;
      if(err?.details){setStockErrors(err.details.map(d=>typeof d==='string'?{name:d,available:0,needed:0}:d));toast.error(`Stock validation failed at ${activeBranchName||'this branch'}`);setPayModal(false);if(activeBranchId)loadInventory(activeBranchId);}
      else toast.error(err?.error||'Sale failed');
    }
  };

  const openSession=async()=>{if(!canOpenSession){toast.error('No permission to open a session');return;}try{const{data}=await posAPI.openSession({opening_balance:parseFloat(openBal)||0});setSession(data);setModal(null);toast.success(`Session opened — ${data.branch?.name||activeBranchName||'branch'}`);if(data.branch_id)loadInventory(data.branch_id);}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const closeSession=async()=>{
    if(!canCloseSession){toast.error('No permission to close this session');return;}
    try{
      await posAPI.closeSession(session.id,{closing_balance:parseFloat(closeBal)||0,notes:closeNotes});
      setSession(null);setCart([]);setInventory([]);setInventoryBranchId(null);setModal(null);setCloseNotes('');
      toast.success('Session closed successfully');
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}
  };

  const toggleFullscreen=()=>{
    if(!document.fullscreenElement){
      document.documentElement.requestFullscreen().then(()=>setIsFullscreen(true)).catch(()=>{});
    } else {
      document.exitFullscreen().then(()=>setIsFullscreen(false)).catch(()=>{});
    }
  };
  useEffect(()=>{
    const h=()=>setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange',h);
    return()=>document.removeEventListener('fullscreenchange',h);
  },[]);

  // Compute session summary for close dialog
  const sessionSalesUSD=txnHistory.filter(t=>t.type==='sale'&&t.status!=='voided').reduce((s,t)=>s+parseFloat(t.total_amount||0),0);
  const sessionSalesKHR=txnHistory.filter(t=>t.type==='sale'&&t.status!=='voided').reduce((s,t)=>s+parseFloat(t.total_amount_khr||t.total_amount*getKhrRate()||0),0);
  const sessionRefunds=txnHistory.filter(t=>t.type==='refund').reduce((s,t)=>s+parseFloat(t.total_amount||0),0);
  const sessionTxnCount=txnHistory.filter(t=>t.status!=='voided').length;
  const sessionExpected=parseFloat(session?.opening_balance||0)+sessionSalesUSD-sessionRefunds;
  const sessionKhrCollected=txnHistory.filter(t=>t.type==='sale'&&t.status!=='voided').reduce((s,t)=>{
    const khrPayments=(t.payments||[]).filter(p=>(p.original_currency||p.currency)==='KHR');
    return s+khrPayments.reduce((a,p)=>a+(parseFloat(p.original_amount||0)),0);
  },0);
  const sessionUsdCollected=txnHistory.filter(t=>t.type==='sale'&&t.status!=='voided').reduce((s,t)=>{
    const usdPayments=(t.payments||[]).filter(p=>(p.original_currency||p.currency||'USD')==='USD');
    return s+usdPayments.reduce((a,p)=>a+parseFloat(p.amount||0),0);
  },0);

  const custOpts=[{value:'',label:'🚶 Walk-in Customer'},...customers.map(c=>({value:String(c.id),label:c.name+(c.discount_percent>0?` (${c.discount_percent}% disc)`:'')}))];
  const methOpts=methods.map(m=>({value:String(m.id),label:m.name}));
  const currOpts=[{value:'USD',label:'USD ($)'},{value:'KHR',label:'KHR (៛)'}];

  if(!canAccess&&!canOpenSession)return(
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'calc(100vh - 120px)',padding:16}}>
      <div style={{textAlign:'center',maxWidth:360}}><div style={{width:64,height:64,borderRadius:16,background:'var(--dm)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}><Lock size={28} color="var(--danger)"/></div><h3 style={{marginBottom:8,color:'var(--danger)'}}>POS Access Restricted</h3><p style={{color:'var(--td)',fontSize:13}}>You don't have permission to access the POS terminal.</p></div>
    </div>
  );

  if(!session)return(
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'calc(100vh - 120px)',padding:16}}>
      {/* Back to Dashboard button — top left */}
      <button onClick={()=>navigate('/')}
        style={{position:'fixed',top:16,left:16,display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:10,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--td)',cursor:'pointer',fontSize:12,fontWeight:600,zIndex:100,transition:'color .15s,border-color .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.color='var(--primary)';e.currentTarget.style.borderColor='var(--primary)'}}
        onMouseLeave={e=>{e.currentTarget.style.color='var(--td)';e.currentTarget.style.borderColor='var(--border)'}}>
        <Home size={13}/> Dashboard
      </button>
      <div className="card" style={{padding:isMobile?28:48,textAlign:'center',maxWidth:440,width:'100%'}}>
        <div style={{width:72,height:72,borderRadius:18,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}><ShoppingCart size={36} color="var(--primary)"/></div>
        <h2 style={{marginBottom:8,fontSize:isMobile?18:22}}>{t('pos.openSession')}</h2>
        {(myBranch||activeBranchName)&&<div style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:20,background:'var(--pm)',border:'1.5px solid var(--primary)',marginBottom:16,fontSize:13,fontWeight:700,color:'var(--primary)'}}><Building2 size={14}/>{myBranch?.name||activeBranchName}{!isOwner&&<Lock size={11}/>}</div>}
        {!canOpenSession?<div style={{padding:'12px 16px',background:'var(--dm)',borderRadius:10,marginBottom:16}}><div style={{display:'flex',alignItems:'center',gap:8,color:'var(--danger)',fontWeight:700,fontSize:13}}><AlertTriangle size={16}/>No permission to open a session</div></div>:(
          <><p className="text-muted" style={{fontSize:13,marginBottom:24}}>Enter opening cash balance to start selling.</p>
          <FormInput label={t('pos.openingBalance')} value={openBal} onChange={setOpenBal} type="number"/>
          <div className="text-dim" style={{fontSize:12,marginBottom:20}}>≈ {fmtKHR(parseFloat(openBal||0)*getKhrRate())} KHR</div>
          <button className="btn btn-primary btn-lg" style={{width:'100%',justifyContent:'center',padding:'14px 0',fontSize:16,borderRadius:12}} onClick={openSession}><LogIn size={18}/> {t('pos.openRegister')}</button></>
        )}
        {/* Back link inside card */}
        <button onClick={()=>navigate('/')}
          style={{display:'inline-flex',alignItems:'center',gap:5,marginTop:16,padding:'8px 16px',borderRadius:8,border:'1px solid var(--border)',background:'transparent',color:'var(--td)',cursor:'pointer',fontSize:12,fontWeight:600,width:'100%',justifyContent:'center'}}>
          <Home size={13}/> Back to Dashboard
        </button>
      </div>
    </div>
  );

  const branchMismatch=inventoryBranchId&&session?.branch_id&&inventoryBranchId!==session.branch_id;

  const SessionBar=(
    <div style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',background:branchMismatch?'var(--dm)':'var(--sm)',borderBottom:`1px solid ${branchMismatch?'rgba(239,68,68,.3)':'rgba(34,197,94,.2)'}`,fontSize:11,flexShrink:0}}>
      {/* ── Back navigation buttons ── */}
      <button
        onClick={()=>navigate('/pos-sessions')}
        title="POS Sessions"
        style={{display:'flex',alignItems:'center',gap:4,padding:'3px 9px',borderRadius:6,border:'1px solid rgba(34,197,94,.3)',background:'rgba(34,197,94,.08)',color:'var(--success)',cursor:'pointer',fontSize:11,fontWeight:700,flexShrink:0,transition:'all .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.background='rgba(34,197,94,.18)';}}
        onMouseLeave={e=>{e.currentTarget.style.background='rgba(34,197,94,.08)';}}
      >
        <History size={11}/> POS Sessions
      </button>
      <button
        onClick={()=>navigate('/')}
        title="Dashboard"
        style={{display:'flex',alignItems:'center',gap:4,padding:'3px 9px',borderRadius:6,border:'1px solid rgba(79,140,255,.3)',background:'rgba(79,140,255,.08)',color:'var(--primary)',cursor:'pointer',fontSize:11,fontWeight:700,flexShrink:0,transition:'all .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.background='rgba(79,140,255,.18)';}}
        onMouseLeave={e=>{e.currentTarget.style.background='rgba(79,140,255,.08)';}}
      >
        <Home size={11}/> {t('nav.dashboard')}
      </button>
      {/* ── Divider ── */}
      <span style={{color:'var(--td)',opacity:.4,fontSize:14}}>|</span>
      {/* ── Session info ── */}
      {branchMismatch
        ?<><AlertTriangle size={12} color="var(--danger)"/><span style={{color:'var(--danger)',fontWeight:700}}>Branch mismatch — reloading…</span></>
        :<>
          <Building2 size={12} color="var(--success)"/>
          <span style={{color:'var(--success)',fontWeight:700}}>{session.branch?.name||activeBranchName||'Branch'}</span>
          <span style={{color:'var(--td)',opacity:.5}}>·</span>
          <span style={{color:'var(--td)'}}>Session #{session.id}</span>
          <span style={{color:'var(--td)',opacity:.5}}>·</span>
          <span style={{color:'var(--td)'}}>{user?.first_name} {user?.last_name}</span>
          <span style={{color:'var(--td)',opacity:.5}}>·</span>
          <Warehouse size={11} color="var(--td)"/>
          <span style={{color:'var(--td)'}}>Stock: {session.branch?.name||activeBranchName}</span>
          {!isOwner&&<Lock size={10} color="var(--td)"/>}
        </>
      }
    </div>
  );

  const ProductGrid=(
    <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(auto-fill,minmax(130px,1fr))':'repeat(auto-fill,minmax(150px,1fr))',gap:isMobile?8:10}}>
      {filtered.length===0&&<div style={{gridColumn:'1/-1',textAlign:'center',padding:60,color:'var(--td)'}}><Package size={40} color="var(--border)" style={{marginBottom:12}}/><div>No products found</div></div>}
      {filtered.map(p=>{const stock=getStock(p.id);const out=!p.allow_negative_stock&&stock!==null&&stock<=0;const low=!p.allow_negative_stock&&stock!==null&&stock>0&&stock<=10;const cartItem=cart.find(i=>i.product_id===p.id);const stockError=stockErrors.find(e=>e.product_id===p.id);return(
        <div key={p.id} onClick={()=>!out&&addToCart(p)} style={{borderRadius:10,overflow:'hidden',background:'var(--surface)',border:`1px solid ${stockError?'var(--danger)':cartItem?'var(--primary)':out?'rgba(239,68,68,.4)':'var(--border)'}`,cursor:out?'not-allowed':'pointer',opacity:out?.5:1,transform:cartItem?'scale(1.02)':'scale(1)',transition:'all .15s',position:'relative',boxShadow:stockError?'0 0 0 2px rgba(239,68,68,.3)':cartItem?'0 0 0 2px var(--primary)33':'none'}}>
          {cartItem&&<div style={{position:'absolute',top:6,left:6,zIndex:2,width:22,height:22,borderRadius:'50%',background:'var(--primary)',color:'#fff',fontWeight:900,fontSize:11,display:'flex',alignItems:'center',justifyContent:'center'}}>{cartItem.quantity}</div>}
          <div style={{height:isMobile?80:100,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center',position:'relative',overflow:'hidden'}}>
            {p.image_url?<img src={p.image_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<Package size={24} color="var(--td)"/>}
            <StockBadge qty={stock} allowNeg={p.allow_negative_stock}/>
          </div>
          <div style={{padding:isMobile?'6px 8px':'8px 10px'}}>
            <div style={{fontSize:isMobile?11:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:4}}>
              <span style={{fontSize:isMobile?13:15,fontWeight:800,color:'var(--primary)'}}>{fmt(p.sale_price)}</span>
              {p.uom?.abbreviation&&<span style={{fontSize:10,color:'var(--td)',background:'var(--s2)',padding:'1px 5px',borderRadius:4}}>{p.uom.abbreviation}</span>}
            </div>
            {!isMobile&&<div style={{fontSize:9,color:'var(--td)',marginTop:1}}>{fmtKHR(parseFloat(p.sale_price)*getKhrRate())} · {p.sku}</div>}
          </div>
        </div>
      )})}
    </div>
  );

  const CartItems=(
    <>{cart.length===0?<div style={{textAlign:'center',padding:isMobile?30:60,color:'var(--td)',fontSize:13}}><ShoppingCart size={36} color="var(--border)" style={{marginBottom:10}}/><div>Tap products to add</div></div>:
      cart.map((item,i)=>{
        const avail=invMap[item.product_id]??null;
        const factor=item.uom_conversion_factor||1;
        const hasStockWarn=!item.allow_negative_stock&&avail!==null&&item.quantity*factor>avail;
        const hasAltUoms=item.alt_uoms&&item.alt_uoms.length>0;
        return(
          <div key={i} style={{padding:'10px 12px',borderRadius:10,background:hasStockWarn?'rgba(239,68,68,.07)':'var(--s2)',marginBottom:6,border:`1px solid ${hasStockWarn?'rgba(239,68,68,.4)':'var(--border)'}`}}>
            <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
              <div style={{width:38,height:38,borderRadius:8,background:'var(--surface)',overflow:'hidden',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                {item.image_url?<img src={item.image_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<Package size={14} color="var(--td)"/>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:4}}>
                  <div style={{fontSize:12,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{item.name}</div>
                  <button onClick={()=>removeItem(i)} style={{background:'none',border:'none',color:'var(--danger)',cursor:'pointer',padding:0,flexShrink:0}}><X size={14}/></button>
                </div>
                <div style={{fontSize:10,color:'var(--td)',marginTop:1}}>{fmt(item.unit_price)}{item.uom_label?` / ${item.uom_label}`:''}</div>
                {hasStockWarn&&<div style={{display:'flex',alignItems:'center',gap:4,fontSize:10,color:'var(--danger)',fontWeight:700,marginTop:3}}><AlertCircle size={10}/>Only {Math.floor(avail/factor)} {item.uom_label||'units'} available at {activeBranchName||'branch'}</div>}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:6,marginTop:8,flexWrap:'wrap'}}>
              <button onClick={()=>updateQty(i,item.quantity-1)} style={{width:30,height:30,borderRadius:8,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Minus size={13}/></button>
              <input type="number" value={item.quantity} onChange={e=>updateQty(i,parseInt(e.target.value)||1)} style={{width:40,textAlign:'center',padding:'4px',borderRadius:6,border:`1px solid ${hasStockWarn?'var(--danger)':'var(--border)'}`,background:'var(--surface)',color:hasStockWarn?'var(--danger)':'var(--text)',fontSize:14,fontWeight:800}}/>
              <button onClick={()=>updateQty(i,item.quantity+1)} style={{width:30,height:30,borderRadius:8,border:'1px solid var(--border)',background:'var(--surface)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Plus size={13}/></button>
              {/* UoM selector */}
              {hasAltUoms&&(
                <select value={String(item.uom_id||'base')} onChange={e=>changeItemUom(i,e.target.value)}
                  style={{fontSize:11,padding:'4px 6px',borderRadius:6,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',cursor:'pointer',minWidth:60}}>
                  <option value="base">{products.find(p=>p.id===item.product_id)?.uom?.abbreviation||'Base'}</option>
                  {item.alt_uoms.map(u=><option key={u.uom_id} value={String(u.uom_id)}>{u.label} (×{u.factor})</option>)}
                </select>
              )}
              {!hasAltUoms&&item.uom_label&&<span style={{fontSize:10,color:'var(--td)',padding:'4px 6px',borderRadius:6,background:'var(--surface)',border:'1px solid var(--border)'}}>{item.uom_label}</span>}
              <div style={{flex:1,display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end'}}>
                {canDiscount&&<input type="number" value={item.discount_pct||''} onChange={e=>updateItemDiscount(i,e.target.value,'pct')} placeholder="%" style={{width:36,padding:'4px',borderRadius:5,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--warning)',fontSize:10,textAlign:'center'}}/>}
                <span style={{fontWeight:800,fontSize:14,color:'var(--primary)'}}>{fmt(item.quantity*item.unit_price-(item.discount_amount||0))}</span>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );

  const CartFooter=(
    <div style={{borderTop:'1px solid var(--border)',background:'var(--s2)'}}>
      {/* Customer select with discount indicator */}
      <div style={{padding:'8px 12px',borderBottom:'1px solid var(--border)'}}>
        <FormInput value={custId} onChange={v=>{setCustId(v);}} options={custOpts}/>
        {selectedCustomer&&selectedCustomer.discount_percent>0&&canDiscount&&(
          <div style={{display:'flex',alignItems:'center',gap:5,marginTop:4,fontSize:11,color:'var(--success)',fontWeight:600}}>
            <Percent size={11}/>{selectedCustomer.discount_percent}% customer discount auto-applied
          </div>
        )}
      </div>
      {cart.length>0&&canDiscount&&<div style={{padding:'6px 12px',borderBottom:'1px solid var(--border)',display:'flex',gap:6,alignItems:'center'}}>
        <Percent size={12} color="var(--warning)"/>
        <span style={{fontSize:11,color:'var(--td)',whiteSpace:'nowrap'}}>Cart Discount:</span>
        <select value={cartDiscountType} onChange={e=>setCartDiscountType(e.target.value)} style={{padding:'3px 5px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',fontSize:11}}>
          <option value="amount">$ Fixed</option><option value="percent">% Percent</option>
        </select>
        <input type="number" value={cartDiscount} onChange={e=>setCartDiscount(e.target.value)} style={{width:55,padding:'3px 6px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--warning)',fontSize:12,textAlign:'right'}}/>
      </div>}
      {cartStockWarnings.length>0&&<div style={{padding:'6px 12px',borderBottom:'1px solid var(--border)',background:'rgba(239,68,68,.07)'}}><div style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:'var(--danger)',fontWeight:700}}><AlertCircle size={12}/>Stock warning at {activeBranchName||'branch'}</div>{cartStockWarnings.map((w,i)=><div key={i} style={{fontSize:10,color:'var(--danger)',marginTop:2}}>· {w.name}: need {w.needed}, have {w.available}</div>)}</div>}
      {stockErrors.length>0&&<div style={{padding:'6px 12px',borderBottom:'1px solid var(--border)',background:'rgba(239,68,68,.07)'}}><div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4,fontWeight:700,color:'var(--danger)',fontSize:12}}><ShieldAlert size={13}/>Stock insufficient at {activeBranchName}</div>{stockErrors.map((e,i)=><div key={i} style={{fontSize:11,color:'var(--danger)'}}>{typeof e==='string'?e:e.name}</div>)}</div>}
      <div style={{padding:'10px 14px'}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:3,fontSize:12}}><span style={{color:'var(--td)'}}>Subtotal</span><span>{fmt(subtotal)}</span></div>
        {totalDiscount>0&&<div style={{display:'flex',justifyContent:'space-between',marginBottom:3,fontSize:12}}><span style={{color:'var(--warning)'}}>Discount</span><span style={{color:'var(--warning)'}}>-{fmt(totalDiscount)}</span></div>}
        <div style={{display:'flex',justifyContent:'space-between',fontSize:20,fontWeight:900,marginTop:4,marginBottom:2}}><span>Total</span><span style={{color:'var(--primary)'}}>{fmt(total)}</span></div>
        <div style={{textAlign:'right',fontSize:11,color:'var(--accent)',fontWeight:600,marginBottom:10}}>{fmtKHR(totalKHR)}</div>
        <button className="btn btn-primary" disabled={!cart.length||validating||cartStockWarnings.length>0}
          style={{width:'100%',justifyContent:'center',padding:'14px 0',fontSize:16,borderRadius:12,fontWeight:800,opacity:cartStockWarnings.length>0?.6:1}}
          onClick={openPayment}>
          {validating?<><RefreshCw size={16} style={{animation:'spin 1s linear infinite'}}/> Validating…</>
            :cartStockWarnings.length>0?<><AlertCircle size={16}/> Fix stock issues first</>
            :<><DollarSign size={20}/> Charge {fmt(total)}</>}
        </button>
      </div>
    </div>
  );

  const salesCount=txnHistory.filter(t=>t.type==='sale'&&t.status!=='voided').length;

  const SharedModalsEl=(
    <>
      <Modal open={payModal} onClose={()=>setPayModal(false)} title={t('pos.payment')} width={isMobile?360:500}>
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'var(--sm)',borderRadius:8,marginBottom:14,fontSize:12}}>
          <Building2 size={13} color="var(--success)"/>
          <span style={{color:'var(--success)',fontWeight:600}}>Stock will be deducted from:</span>
          <span style={{color:'var(--text)',fontWeight:700}}>{activeBranchName||'Branch'}</span>
          <Lock size={10} color="var(--success)" style={{marginLeft:'auto'}}/>
        </div>
        {selectedCustomer&&<div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',background:'var(--pm)',borderRadius:8,marginBottom:12,fontSize:12}}>
          <User size={12} color="var(--primary)"/>
          <span style={{color:'var(--primary)',fontWeight:600}}>{selectedCustomer.name}</span>
          {selectedCustomer.discount_percent>0&&<span style={{color:'var(--success)',fontSize:11}}>({selectedCustomer.discount_percent}% discount applied)</span>}
        </div>}
        <div style={{textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:11,color:'var(--td)',marginBottom:2}}>Total Due</div>
          <div style={{fontSize:36,fontWeight:900,color:'var(--primary)'}}>{fmt(total)}</div>
          <div style={{fontSize:15,color:'var(--accent)',fontWeight:600}}>{fmtKHR(totalKHR)}</div>
        </div>
        <div style={{marginBottom:12}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}><span style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.4}}>{t('pos.paymentMethods')}</span><button className="btn btn-ghost btn-sm" onClick={addPayLine}><Plus size={12}/> Add</button></div>
          {payLines.map((line,i)=>(
            <div key={i} style={{display:'grid',gridTemplateColumns:'1.5fr 1fr 1.5fr auto',gap:6,marginBottom:6,alignItems:'end'}}>
              <FormInput label={i===0?'Method':undefined} value={String(line.method_id)} onChange={v=>updatePayLine(i,'method_id',v)} options={methOpts}/>
              <FormInput label={i===0?'Currency':undefined} value={line.currency} onChange={v=>{updatePayLine(i,'currency',v);if(v==='KHR'&&line.currency==='USD')updatePayLine(i,'amount',String(Math.round(parseFloat(line.amount||0)*getKhrRate())));if(v==='USD'&&line.currency==='KHR')updatePayLine(i,'amount',String((parseFloat(line.amount||0)/getKhrRate()).toFixed(2)))}} options={currOpts}/>
              <FormInput label={i===0?'Amount':undefined} value={line.amount} onChange={v=>updatePayLine(i,'amount',v)} type="number"/>
              {payLines.length>1&&<button onClick={()=>removePayLine(i)} style={{background:'none',border:'none',color:'var(--danger)',cursor:'pointer',padding:'8px 0'}}><X size={14}/></button>}
            </div>
          ))}
        </div>
        <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:14}}>
          {[1,5,10,20,50,100].map(a=>(<button key={a} className="btn btn-ghost btn-sm" style={{fontSize:12,padding:'5px 10px'}} onClick={()=>setPayLines([{method_id:methods[0]?.id||'',currency:'USD',amount:String(a)}])}>${a}</button>))}
          <button className="btn btn-ghost btn-sm" style={{fontSize:12,color:'var(--primary)',padding:'5px 10px'}} onClick={()=>setPayLines([{method_id:methods[0]?.id||'',currency:'USD',amount:String(total.toFixed(2))}])}>Exact</button>
          {[5000,10000,20000,50000].map(a=>(<button key={a} className="btn btn-ghost btn-sm" style={{fontSize:12,padding:'5px 10px'}} onClick={()=>setPayLines([{method_id:methods[0]?.id||'',currency:'KHR',amount:String(a)}])}>៛{a>=1000?`${a/1000}K`:a}</button>))}
        </div>
        <div style={{padding:12,background:'var(--s2)',borderRadius:10,marginBottom:14}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4,fontSize:13}}><span style={{color:'var(--td)'}}>Total Due</span><span style={{fontWeight:700}}>{fmt(total)}</span></div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4,fontSize:13}}><span style={{color:'var(--td)'}}>Paid (USD equiv.)</span><span style={{fontWeight:700,color:'var(--success)'}}>{fmt(paidUSD)}</span></div>
          {paidUSD>=total&&<div style={{display:'flex',justifyContent:'space-between',paddingTop:8,borderTop:'1px solid var(--border)'}}><span style={{fontWeight:700,color:'var(--accent)'}}>Change</span><div style={{textAlign:'right'}}><div style={{fontWeight:900,color:'var(--accent)',fontSize:22}}>{fmt(changeUSD)}</div><div style={{fontSize:12,color:'var(--accent)'}}>{fmtKHR(changeUSD*getKhrRate())}</div></div></div>}
          {paidUSD<total-0.01&&<div style={{color:'var(--danger)',fontSize:12,marginTop:6}}>⚠️ Short by {fmt(total-paidUSD)}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setPayModal(false)}>{t('common.cancel')}</button>
          <button className="btn btn-primary btn-lg" onClick={checkout} disabled={paidUSD<total-0.01} style={{flex:1,justifyContent:'center',fontSize:16,fontWeight:800}}><CheckCircle size={18}/> {t('pos.completeSale')}</button>
        </div>
      </Modal>

      <Modal open={modal==='close'} onClose={()=>setModal(null)} title="{t('pos.closeSession')}" width={500}>
        {/* Branch pill */}
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',background:'var(--pm)',borderRadius:8,marginBottom:14,fontSize:12}}>
          <Building2 size={14} color="var(--primary)"/>
          <span style={{color:'var(--primary)',fontWeight:600}}>Closing session for:</span>
          <span style={{color:'var(--text)',fontWeight:700}}>{session?.branch?.name||activeBranchName}</span>
          {!isOwner&&<Lock size={10} color="var(--td)" style={{marginLeft:'auto'}}/>}
        </div>
        {!canCloseSession?(
          <div style={{padding:'12px 14px',background:'var(--dm)',borderRadius:8}}>
            <div style={{display:'flex',alignItems:'center',gap:8,color:'var(--danger)',fontWeight:700,fontSize:13}}><AlertTriangle size={16}/>No permission to close this session</div>
          </div>
        ):(
          <>
            {/* Session summary */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:12}}>
              {[
                {label:'Transactions',value:sessionTxnCount,color:'var(--primary)',sub:null},
                {label:'Total Sales',value:fmt(sessionSalesUSD),color:'var(--success)',sub:sessionSalesKHR>0?`≈ ៛${Math.round(sessionSalesKHR).toLocaleString()}`:null},
                {label:'Opening Bal.',value:fmt(session?.opening_balance||0),color:'var(--text)',sub:null},
              ].map(({label,value,color,sub})=>(
                <div key={label} style={{background:'var(--s2)',borderRadius:10,padding:'10px 12px',border:'1px solid var(--border)'}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>{label}</div>
                  <div style={{fontSize:15,fontWeight:800,color}}>{value}</div>
                  {sub&&<div style={{fontSize:10,color:'var(--td)',marginTop:2}}>{sub}</div>}
                </div>
              ))}
            </div>
            {/* Currency breakdown */}
            {(sessionUsdCollected>0||sessionKhrCollected>0)&&(
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                <div style={{background:'rgba(34,197,94,.07)',borderRadius:10,padding:'10px 12px',border:'1px solid rgba(34,197,94,.2)'}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>💵 USD Collected</div>
                  <div style={{fontSize:16,fontWeight:800,color:'var(--success)'}}>{fmt(sessionUsdCollected)}</div>
                </div>
                <div style={{background:'rgba(234,179,8,.07)',borderRadius:10,padding:'10px 12px',border:'1px solid rgba(234,179,8,.2)'}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>🏦 KHR Collected</div>
                  <div style={{fontSize:16,fontWeight:800,color:'var(--warning)'}}>៛{Math.round(sessionKhrCollected).toLocaleString()}</div>
                  <div style={{fontSize:10,color:'var(--td)',marginTop:2}}>≈ {fmt(sessionKhrCollected/getKhrRate())}</div>
                </div>
              </div>
            )}
            {/* Expected balance */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:'var(--pm)',borderRadius:10,marginBottom:12,border:'1px solid var(--primary)33'}}>
              <div style={{display:'flex',alignItems:'center',gap:6}}><Info size={14} color="var(--primary)"/><span style={{fontSize:12,fontWeight:700,color:'var(--primary)'}}>Expected Balance</span></div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:16,fontWeight:900,color:'var(--primary)'}}>{fmt(sessionExpected)}</div>
                <div style={{fontSize:10,color:'var(--td)'}}>Opening + Sales − Refunds</div>
              </div>
            </div>
            {/* Closing balance — auto-filled */}
            <div style={{marginBottom:12}}>
              <label style={{fontSize:12,fontWeight:700,color:'var(--td)',display:'block',marginBottom:5}}>
                Closing Balance (USD)
                <span style={{marginLeft:6,fontSize:10,color:'var(--success)',fontWeight:600}}>← auto-filled from expected</span>
              </label>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <input type="number" value={closeBal} onChange={e=>setCloseBal(e.target.value)}
                  style={{flex:1,padding:'10px 12px',borderRadius:9,border:'2px solid var(--primary)',background:'var(--s2)',color:'var(--text)',fontSize:16,fontWeight:800,textAlign:'right'}}/>
                <button className="btn btn-ghost btn-sm" onClick={()=>setCloseBal(sessionExpected.toFixed(2))} title="Reset to expected" style={{flexShrink:0,fontSize:11}}>Reset</button>
              </div>
              {closeBal!==''&&parseFloat(closeBal)!==sessionExpected&&(
                <div style={{marginTop:5,display:'flex',alignItems:'center',gap:5,fontSize:12,fontWeight:700,
                  color:parseFloat(closeBal)>sessionExpected?'var(--success)':'var(--danger)'}}>
                  {parseFloat(closeBal)>sessionExpected?<TrendingUp size={13}/>:<AlertTriangle size={13}/>}
                  {parseFloat(closeBal)>sessionExpected?'Over':'Short'} by {fmt(Math.abs(parseFloat(closeBal||0)-sessionExpected))}
                </div>
              )}
              {closeBal!==''&&<div style={{fontSize:11,color:'var(--td)',marginTop:3}}>≈ ៛{Math.round(parseFloat(closeBal||0)*getKhrRate()).toLocaleString()}</div>}
            </div>
            {/* Notes */}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:700,color:'var(--td)',display:'flex',alignItems:'center',gap:5,marginBottom:5}}>
                <StickyNote size={12}/> Notes (optional)
              </label>
              <textarea value={closeNotes} onChange={e=>setCloseNotes(e.target.value)}
                placeholder="e.g. Cash verified, discrepancy noted, end-of-day remarks…"
                rows={2}
                style={{width:'100%',padding:'9px 12px',borderRadius:9,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13,resize:'vertical',boxSizing:'border-box',lineHeight:1.5}}/>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>{t('common.cancel')}</button>
              <button className="btn btn-danger" onClick={closeSession} style={{gap:6,display:'flex',alignItems:'center'}}><LogOut size={14}/> {t('pos.closeRegister')}</button>
            </div>
          </>
        )}
      </Modal>

      {/* Receipt print modal */}
      {printTxn&&<ReceiptModal txn={printTxn} business={business} branch={session?.branch||myBranch} onClose={()=>setPrintTxn(null)}/>}
    </>
  );

  if(!isMobile){
    const cartW=isFullscreen?420:400;
    return(
      <div style={{
        position:'fixed',
        inset:0,
        zIndex:9000,
        display:'grid',
        gridTemplateColumns:`1fr ${cartW}px`,
        height:'100vh',
        margin:0,
        background:'var(--background)',
        overflow:'hidden',
      }}>
        {/* ── LEFT: Product panel ── */}
        <div style={{display:'flex',flexDirection:'column',overflow:'hidden',background:'var(--background)'}}>
          {SessionBar}
          {/* Toolbar */}
          <div style={{padding:'8px 12px',background:'var(--surface)',borderBottom:'1px solid var(--border)',display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
            {/* Search */}
            {tab==='products'&&(
              <div style={{position:'relative',flex:1}}>
                <Search size={15} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--td)',pointerEvents:'none'}}/>
                <input
                  ref={searchRef}
                  className="form-input"
                  value={search}
                  onChange={e=>setSearch(e.target.value)}
                  placeholder={t('pos.searchProducts')}
                  style={{paddingLeft:34,fontSize:13,height:36}}
                  onKeyDown={e=>{
                    if(e.key==='Enter'&&filtered.length===1){addToCart(filtered[0]);setSearch('');}
                    if(e.key==='Escape'){setSearch('');searchRef.current?.blur();}
                  }}
                  autoComplete="off"
                />
                {search&&<button onClick={()=>{setSearch('');searchRef.current?.focus();}} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--td)',padding:0,display:'flex'}}><X size={13}/></button>}
              </div>
            )}
            {tab==='orders'&&<div style={{flex:1,fontSize:14,fontWeight:700,color:'var(--text)'}}>{t('pos.orders')}</div>}
            {/* Tab buttons */}
            <button className={`filter-btn ${tab==='products'?'active':''}`} onClick={()=>{setTab('products');}} style={{padding:'6px 12px',fontSize:12,gap:5,display:'flex',alignItems:'center',flexShrink:0,height:36}}>
              <Package size={13}/> {t('pos.products')}
            </button>
            <button className={`filter-btn ${tab==='orders'?'active':''}`} onClick={()=>{setTab('orders');loadHistory();}} style={{padding:'6px 12px',fontSize:12,gap:5,display:'flex',alignItems:'center',flexShrink:0,height:36}}>
              <Receipt size={13}/> {t('pos.orders')}
              {salesCount>0&&<span style={{background:'var(--primary)',color:'#fff',borderRadius:10,padding:'1px 6px',fontSize:10,fontWeight:700,marginLeft:2}}>{salesCount}</span>}
            </button>
            {lastTxn&&<button className="btn btn-ghost btn-sm" onClick={()=>setPrintTxn(lastTxn)} style={{gap:4,color:'var(--primary)',flexShrink:0,height:36}} title={t('pos.reprintReceipt')}><Printer size={14}/></button>}
            {/* Fullscreen toggle */}
            <button className="btn btn-ghost btn-sm" onClick={toggleFullscreen} title={isFullscreen?t('common.exitFullscreen'):t('common.fullscreen')} style={{padding:'7px 8px',flexShrink:0,height:36}}>
              {isFullscreen?<Minimize2 size={15}/>:<Maximize2 size={15}/>}
            </button>
            {canCloseSession&&<button className="btn btn-danger btn-sm" onClick={()=>{setCloseBal(sessionExpected.toFixed(2));setCloseNotes('');loadHistory();setModal('close');}} title={t('pos.closeSession')} style={{flexShrink:0,height:36}}><LogOut size={15}/></button>}
          </div>
          {/* Category filter pills */}
          {tab==='products'&&(
            <div style={{padding:'6px 12px',background:'var(--s2)',borderBottom:'1px solid var(--border)',display:'flex',gap:5,overflowX:'auto',flexShrink:0,scrollbarWidth:'none'}}>
              <button className={`filter-btn ${catFilter==='all'?'active':''}`} onClick={()=>setCatFilter('all')} style={{whiteSpace:'nowrap',fontSize:11,padding:'4px 12px',flexShrink:0,display:'flex',alignItems:'center',gap:4}}>
                <Layers size={11}/> {t('common.all')}
              </button>
              {categories.filter(c=>c.is_active).map(c=>(
                <button key={c.id} className={`filter-btn ${catFilter===String(c.id)?'active':''}`} onClick={()=>setCatFilter(String(c.id))}
                  style={{whiteSpace:'nowrap',fontSize:11,padding:'4px 12px',flexShrink:0,display:'flex',alignItems:'center',gap:4}}>
                  {c.image_url&&<img src={c.image_url} alt="" style={{width:13,height:13,borderRadius:2,objectFit:'cover'}}/>}
                  {c.name}
                </button>
              ))}
            </div>
          )}
          {/* Content area */}
          <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>
            {tab==='products'&&<div style={{flex:1,overflowY:'auto',padding:isFullscreen?14:12}}>{ProductGrid}</div>}
            {tab==='orders'&&<OrdersTab txnHistory={txnHistory} loadHistory={loadHistory} loading={txnLoading} isMobile={false} fmtKHR={fmtKHR} getKhrRate={getKhrRate} canVoid={canVoid} branchName={activeBranchName} business={business} branch={session?.branch||myBranch}/>}
          </div>
        </div>

        {/* ── RIGHT: Cart panel ── */}
        <div style={{background:'var(--surface)',borderLeft:'1px solid var(--border)',display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
          {/* Cart header */}
          <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',background:'var(--s2)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
            <h4 style={{fontSize:15,fontWeight:800,display:'flex',alignItems:'center',gap:7,margin:0}}>
              <ShoppingCart size={17}/>
              {t('pos.cart')}
              <span style={{fontSize:12,color:'var(--td)',fontWeight:400}}>({cartCount} {t('common.results')})</span>
            </h4>
            {cart.length>0&&<button className="btn btn-ghost btn-sm" onClick={clearCart} style={{fontSize:11,color:'var(--danger)'}}><RotateCcw size={11}/> {t('pos.clearCart')}</button>}
          </div>
          <div style={{flex:1,overflowY:'auto',padding:10}}>{CartItems}</div>
          {CartFooter}
        </div>
        {SharedModalsEl}
      </div>
    );
  }

  return(
    <div style={{height:'100vh',display:'flex',flexDirection:'column',margin:0,overflow:'hidden',background:'var(--background)'}}>
      {SessionBar}
      <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',padding:'10px 14px',display:'flex',gap:8,alignItems:'center',flexShrink:0,zIndex:10}}>
        {tab==='cart'&&<button style={{background:'none',border:'none',cursor:'pointer',color:'var(--text)',padding:0}} onClick={()=>setTab('products')}><ArrowLeft size={20}/></button>}
        {tab!=='cart'&&<div style={{position:'relative',flex:1}}><Search size={14} style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'var(--td)'}}/><input className="form-input" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products..." style={{paddingLeft:30,fontSize:13,padding:'8px 10px 8px 30px'}}/></div>}
        {tab==='cart'&&<div style={{flex:1,fontSize:14,fontWeight:700}}>Cart ({cartCount})</div>}
        {lastTxn&&<button className="btn btn-ghost btn-sm" onClick={()=>setPrintTxn(lastTxn)} style={{padding:'7px 8px',color:'var(--primary)'}}><Printer size={14}/></button>}
        <button className="btn btn-ghost btn-sm" onClick={toggleFullscreen} style={{padding:'7px 8px'}}>
          {isFullscreen?<Minimize2 size={14}/>:<Maximize2 size={14}/>}
        </button>
        {canCloseSession&&<button className="btn btn-danger btn-sm" onClick={()=>{setCloseBal(sessionExpected.toFixed(2));setCloseNotes('');loadHistory();setModal('close')}} style={{padding:'7px 10px'}}><LogOut size={14}/></button>}
      </div>
      <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>
        {tab==='products'&&<>
          <div style={{padding:'8px 12px',background:'var(--surface)',borderBottom:'1px solid var(--border)',display:'flex',gap:5,overflowX:'auto',flexShrink:0}}>
            <button className={`filter-btn ${catFilter==='all'?'active':''}`} onClick={()=>setCatFilter('all')} style={{whiteSpace:'nowrap',fontSize:11,padding:'5px 11px',flexShrink:0}}><Layers size={10}/> All</button>
            {categories.filter(c=>c.is_active).map(c=><button key={c.id} className={`filter-btn ${catFilter===String(c.id)?'active':''}`} onClick={()=>setCatFilter(String(c.id))} style={{whiteSpace:'nowrap',fontSize:11,padding:'5px 11px',flexShrink:0}}>{c.name}</button>)}
          </div>
          <div style={{flex:1,overflowY:'auto',padding:'10px 12px 80px'}}>{ProductGrid}</div>
        </>}
        {tab==='cart'&&<><div style={{flex:1,overflowY:'auto',padding:'10px 12px 100px'}}>{CartItems}</div><div style={{position:'fixed',bottom:60,left:0,right:0,zIndex:20}}>{CartFooter}</div></>}
        {tab==='orders'&&<OrdersTab txnHistory={txnHistory} loadHistory={loadHistory} loading={txnLoading} isMobile={true} fmtKHR={fmtKHR} getKhrRate={getKhrRate} canVoid={canVoid} branchName={activeBranchName} business={business} branch={session?.branch||myBranch}/>}
      </div>
      <div style={{position:'fixed',bottom:0,left:0,right:0,background:'var(--surface)',borderTop:'1px solid var(--border)',display:'flex',height:60,zIndex:30,boxShadow:'0 -4px 20px rgba(0,0,0,.15)'}}>
        <button onClick={()=>setTab('products')} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:3,border:'none',cursor:'pointer',background:'transparent',color:tab==='products'?'var(--primary)':'var(--td)',padding:0}}><Package size={20}/><span style={{fontSize:10,fontWeight:600}}>Products</span></button>
        <button onClick={()=>setTab('cart')} style={{flex:1.4,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,border:'none',cursor:'pointer',background:'transparent',padding:0,position:'relative'}}>
          <div style={{width:52,height:52,borderRadius:'50%',background:cart.length?'var(--primary)':'var(--s2)',border:cart.length?'3px solid var(--primary)':'3px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',position:'absolute',top:-18,transition:'all .2s',boxShadow:cart.length?'0 4px 16px var(--primary)44':'none'}}>
            <ShoppingCart size={22} color={cart.length?'#fff':'var(--td)'}/>
            {cartCount>0&&<div style={{position:'absolute',top:-4,right:-4,width:20,height:20,borderRadius:'50%',background:'var(--danger)',color:'#fff',fontSize:11,fontWeight:900,display:'flex',alignItems:'center',justifyContent:'center'}}>{cartCount>99?'99+':cartCount}</div>}
          </div>
          <span style={{fontSize:10,fontWeight:600,color:tab==='cart'?'var(--primary)':'var(--td)',marginTop:20}}>{cart.length?fmt(total):'Cart'}</span>
        </button>
        <button onClick={()=>{setTab('orders');loadHistory()}} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:3,border:'none',cursor:'pointer',background:'transparent',color:tab==='orders'?'var(--primary)':'var(--td)',padding:0,position:'relative'}}>
          <Receipt size={20}/>
          {salesCount>0&&<div style={{position:'absolute',top:8,right:'calc(50% - 16px)',width:16,height:16,borderRadius:'50%',background:'var(--primary)',color:'#fff',fontSize:9,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>{salesCount}</div>}
          <span style={{fontSize:10,fontWeight:600}}>Orders</span>
        </button>
      </div>
      {SharedModalsEl}
    </div>
  );
}
